import {
    defineStore
} from 'pinia';
import axios from '@/api/config/axios';
import { fetchHumanTextReviewDocumento } from '@/api/humanTextReview';
import {
    appendPdfGeracaoOpcoesToFormData,
    buildPdfGeracaoOpcoesJsonBody,
} from '@/constants/pdfGeracaoOpcoes';
import { FEEDBACK_AUDIO_CORRECAO } from '@/constants/feedbackAudioAttachableTypes';
import { executeWithPlanOverageConsentRetry } from '@/composables/usePlanOverageConsent';
import { normalizeCorrecaoAvaliacoesAliases, normalizeDocumentoCorrecoesAvaliacoes } from '@/utils/normalizeCorrecaoAvaliacoes';

/**
 * Evita chamadas como GET /documento/undefined quando o id da rota ainda não
 * está disponível ou veio interpolado como string "undefined"/"null".
 */
export function isValidDocumentoApiId(id) {
    if (id == null || id === '') return false;
    const s = String(id).trim();
    if (!s) return false;
    const lower = s.toLowerCase();
    if (lower === 'undefined' || lower === 'null') return false;
    return true;
}

export const useDocumentoStore = defineStore('documentoStore', {
    state: () => ({
        documentos: [],
        totalDocumentos: 0,
        temRascunho: false,
        pagination: {
            total: 0,
            per_page: 10,
            current_page: 1,
            last_page: 1,
            from: null,
            to: null
        },
        documento: {
            id: null,
            tipo: '',
            correcao: '',
            correcao_midia: null,
            correcao_file: null,
            texto_redacao: '',
            path: null,
            /** Segunda imagem (parte de baixo); enviada como `file_parte_inferior` para mesclagem no servidor. */
            path_parte_inferior: null,
            paciente: null,
            turma: null,
            tema: null,
        },
        pdfUrl: '',
        error: null,
        items: [],
        audioRemovido: false,
        // Estados para rotação de imagem
        rotatingImage: false,
        imageRotationError: null,
    }),

    getters: {
        getDocumentos: (state) => ({
            data: state.documentos,
            pagination: state.pagination
        }),
        
        currentPage: (state) => state.pagination.current_page,
        totalPages: (state) => state.pagination.last_page,
        totalRegistros: (state) => state.pagination.total,
        registrosPorPagina: (state) => state.pagination.per_page,
        
        getDocumento: (state) => state.documento,
        getError: (state) => state.error,
        isRotatingImage: (state) => state.rotatingImage,
        getImageRotationError: (state) => state.imageRotationError,
    },

    actions: {
        async fetchDocumentos(params = {}) {
            try {
                const response = await axios.get('/api/v1/documento', { 
                    params: {
                        page: params.page || 1,
                        per_page: params.per_page || 10,
                        search: params.search || '',
                        ...params
                    }
                });
                
                const { data, pagination } = response.data.data;
                
                this.documentos = data;
                this.pagination = pagination;
            } catch (error) {
                this.error = 'Erro ao obter documentos';
                console.error('Erro na busca de documentos:', error);
                throw error;
            }
        },

        async obterDocumento(documentoId, options = {}) {
            if (!isValidDocumentoApiId(documentoId)) return;
            this.error = null;
            try {
                const params = options.bustCache ? { _: Date.now() } : undefined;
                const response = await axios.get(`/api/v1/documento/${documentoId}`, { params });
                if (response.status === 200 && response.data.data) {
                    this.documento = response.data.data;
                    normalizeDocumentoCorrecoesAvaliacoes(this.documento);
                }
            } catch (error) {
                this.error = 'Erro ao obter documento';
                console.error('Erro ao obter documento:', error);
                throw error;
            }
        },

        async obterDocumentoRevisaoTextoHumano(documentoId) {
            if (!isValidDocumentoApiId(documentoId)) return;
            this.error = null;
            try {
                const data = await fetchHumanTextReviewDocumento(documentoId);
                if (data) {
                    this.documento = data;
                    normalizeDocumentoCorrecoesAvaliacoes(this.documento);
                }
            } catch (error) {
                this.error = 'Erro ao obter documento para revisão humana';
                console.error('Erro ao obter documento (revisão humana):', error);
                throw error;
            }
        },

        async deletarDocumento(documentoId) {
            this.error = null;
            try {
                const response = await axios.delete(`/api/v1/documento/${documentoId}`);
                if (response.status === 200 && response.data.data) {
                    this.documento = response.data.data;
                }
            } catch (error) {
                this.error = 'Erro ao deletar documento';
                console.error('Erro ao deletar documento:', error);
                throw error;
            }
        },

        prepareDataForSubmission(data, tipo) {
            // Estrutura correta da API: POST /api/documento
            const payload = {
                user_id: data.paciente?.user?.id || data.paciente?.id || null,
                turma_id: data.turma?.id || null,
                tema_id: data.tema?.id || null,
                texto: data.texto_redacao || null,
                nome: data.nome || null,
                chat_id: data.chat_id || null
            };

            // Adicionar arquivo se existir
            if (data.path) {
                payload.file = data.path;
                console.log('Arquivo encontrado no payload:', data.path);
            } else {
                console.log('Nenhum arquivo encontrado no documento');
            }

            if (data.path_parte_inferior) {
                payload.file_parte_inferior = data.path_parte_inferior;
            }

            return payload;
        },

        /**
         * Une duas partes (imagem ou PDF) no servidor (pré-visualização) e devolve um File PNG único.
         * POST /api/v1/documento/mesclar-imagens-redacao — não cria documento.
         *
         * @param {File} fileTop
         * @param {File} fileBottom
         * @returns {Promise<File>}
         */
        async mesclarImagensDuasPartes(fileTop, fileBottom) {
            if (!(fileTop instanceof File) || !(fileBottom instanceof File)) {
                throw new Error('Arquivos inválidos para mesclagem.');
            }
            const formData = new FormData();
            formData.append('file', fileTop);
            formData.append('file_parte_inferior', fileBottom);

            const res = await axios.post(
                '/api/v1/documento/mesclar-imagens-redacao',
                formData,
                {
                    responseType: 'arraybuffer',
                    timeout: 120000,
                    validateStatus: () => true,
                }
            );

            const status = res.status;
            const ct = String(res.headers['content-type'] || '').toLowerCase();
            const raw = res.data;

            if (status === 200 && raw && raw.byteLength > 0 && ct.includes('image/png')) {
                return new File([raw], 'redacao_unificada.png', { type: 'image/png' });
            }

            let message = 'Não foi possível unir as imagens. Tente novamente.';
            try {
                const txt = new TextDecoder('utf-8').decode(new Uint8Array(raw));
                const j = JSON.parse(txt);
                if (typeof j.message === 'string' && j.message.trim()) {
                    message = j.message.trim();
                }
                const errors = j.data?.errors;
                if (errors && typeof errors === 'object') {
                    const parts = [];
                    for (const v of Object.values(errors)) {
                        if (Array.isArray(v)) {
                            parts.push(...v.map((x) => String(x)).filter(Boolean));
                        } else if (v != null) {
                            parts.push(String(v));
                        }
                    }
                    const joined = parts.map((s) => s.trim()).filter(Boolean).join(' ');
                    if (joined) {
                        message = joined;
                    }
                } else if (typeof j.data?.error === 'string' && j.data.error.trim()) {
                    message = j.data.error.trim();
                }
            } catch {
                /* mantém message padrão */
            }

            const err = new Error(message);
            err.response = { status, data: raw };
            throw err;
        },

        async salvarDocumento(tipo) {
            this.error = null;
            try {
                console.log('Documento antes do envio:', this.documento);
                const payload = this.prepareDataForSubmission(this.documento, tipo);
                console.log('Payload preparado:', payload);
                
                // Se houver arquivo, usar FormData
                if (payload.file) {
                    console.log('Processando arquivo:', payload.file);
                    console.log('Arquivo constructor:', payload.file.constructor.name);
                    console.log('Propriedades do arquivo:', {
                        name: payload.file.name,
                        size: payload.file.size,
                        type: payload.file.type,
                        lastModified: payload.file.lastModified
                    });
                    
                    const formData = new FormData();
                    
                    // Adicionar todos os campos obrigatórios
                    formData.append('user_id', payload.user_id);
                    formData.append('turma_id', payload.turma_id);
                    formData.append('tema_id', payload.tema_id);
                    
                    // Adicionar campos opcionais se existirem
                    if (payload.texto) formData.append('texto', payload.texto);
                    if (payload.nome) formData.append('nome', payload.nome);
                    if (payload.chat_id) formData.append('chat_id', payload.chat_id);
                    
                    // Adicionar o arquivo
                    formData.append('file', payload.file);

                    if (payload.file_parte_inferior) {
                        formData.append('file_parte_inferior', payload.file_parte_inferior);
                    }
                    
                    // Verificar se o arquivo foi adicionado ao FormData
                    console.log('FormData entries:');
                    for (let [key, value] of formData.entries()) {
                        console.log(`${key}:`, value);
                    }
                    
                    console.log('FormData preparado, enviando...');
                    const response = await axios.post('/api/v1/documento', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    this.documento.id = response.data.data.id;
                    return response;
                } else {
                    console.log('Nenhum arquivo encontrado, enviando como JSON');
                    // Se não houver arquivo, enviar como JSON normal
                    const response = await axios.post('/api/v1/documento', payload);
                    this.documento.id = response.data.data.id;
                    return response;
                }
            } catch (error) {
                console.error('Erro ao salvar documento:', error);
                console.error('Response data:', error.response?.data);
                console.error('Response status:', error.response?.status);
                
                // Extrair mensagem de erro específica da API
                let errorMessage = 'Erro ao salvar documento';
                
                if (error.response?.data?.data?.error) {
                    errorMessage = error.response.data.data.error;
                    console.log('Store - Erro extraído de data.data.error:', errorMessage);
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                    console.log('Store - Erro extraído de data.message:', errorMessage);
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                    console.log('Store - Erro extraído de data.error:', errorMessage);
                }
                
                this.error = errorMessage;
                throw error;
            }
        },

        async finalizarCorrecao() {
            this.error = null;
            const url = `/api/v1/documento/${this.documento.id}/finalizar-correcao`;
            try {
                const response = await executeWithPlanOverageConsentRetry(
                    () => axios.post(url),
                    'envios_redacoes'
                );
                this.documento.id = response.data.data.id;
                return response;
            } catch (error) {
                this.error = 'Erro ao finalizar correção';
                console.error('Erro ao finalizar correção:', error);
                throw error;
            }
        },

        /**
         * Salva notas de avaliação (competências e subcritérios) via DocumentoRepository::update
         * Mantém compatibilidade com modelos sem subcritérios.
         *
         * @param {string} documentoId - UUID do documento
         * @param {Array} competencias - Array de competências com notas
         * Ex:
         * [{ id|competencia_id, nota, subcriterios: [{ id|subcriterio_id, nota }] }]
         */
        async salvarNotasAvaliacaoDocumento(documentoId, competencias = []) {
            this.error = null;
            try {
                const competenciasArray = Array.isArray(competencias) ? competencias : [];

                const payload = {
                    isAvaliacao: true,
                    competencias: competenciasArray
                        .filter(Boolean)
                        .map((c) => {
                            const competenciaId = c.competencia_id || c.id;
                            const nota = Number(c.nota || 0);

                            const subcriterios = Array.isArray(c.subcriterios) ? c.subcriterios : [];
                            const temSubcriterios = subcriterios.length > 0;

                            const base = {
                                competencia_id: competenciaId,
                                nota: Number.isFinite(nota) ? nota : 0,
                            };

                            if (!temSubcriterios) {
                                return base;
                            }

                            return {
                                ...base,
                                subcriterios: subcriterios
                                    .filter(Boolean)
                                    .map((sc) => {
                                        const subcriterioId = sc.subcriterio_id || sc.id;
                                        const notaSc = Number(sc.nota || 0);
                                        return {
                                            subcriterio_id: subcriterioId,
                                            nota: Number.isFinite(notaSc) ? notaSc : 0,
                                        };
                                    }),
                            };
                        }),
                };

                const response = await axios.patch(`/api/v1/documento/${documentoId}`, payload);

                // Se a API retornar o documento atualizado, manter store sincronizado
                // IMPORTANTE: aplicar atualização IN-PLACE para não trocar referências de arrays/objetos
                // e evitar resets em watchers do frontend.
                if (response?.data?.data) {
                    const atualizado = response.data.data;

                    if (!this.documento || typeof this.documento !== 'object') {
                        this.documento = atualizado;
                        return response;
                    }

                    // Atualizar campos simples que a UI usa em totais
                    if (atualizado.updated_at) this.documento.updated_at = atualizado.updated_at;
                    if (atualizado.status) this.documento.status = atualizado.status;
                    if (typeof atualizado.nota === 'number') this.documento.nota = atualizado.nota;

                    // Atualizar competências/subcritérios sem substituir o array inteiro
                    if (Array.isArray(atualizado.competencias)) {
                        if (!Array.isArray(this.documento.competencias)) {
                            this.documento.competencias = [];
                        }

                        const locais = this.documento.competencias;

                        atualizado.competencias.forEach((compAtualizada) => {
                            if (!compAtualizada?.id) return;

                            let compLocal = locais.find(c => c?.id === compAtualizada.id);
                            if (!compLocal) {
                                locais.push(compAtualizada);
                                return;
                            }

                            // Atualizar nota/metadata sem trocar referência do objeto
                            if (typeof compAtualizada.nota === 'number') compLocal.nota = compAtualizada.nota;
                            if (compAtualizada.updated_at) compLocal.updated_at = compAtualizada.updated_at;
                            if (typeof compAtualizada.intervalo_nota === 'number') compLocal.intervalo_nota = compAtualizada.intervalo_nota;
                            if (typeof compAtualizada.nota_maxima === 'number') compLocal.nota_maxima = compAtualizada.nota_maxima;

                            // Comentários (se vierem)
                            if (Array.isArray(compAtualizada.comentarios)) {
                                compLocal.comentarios = compAtualizada.comentarios;
                            }

                            // Subcritérios
                            if (Array.isArray(compAtualizada.subcriterios)) {
                                if (!Array.isArray(compLocal.subcriterios)) compLocal.subcriterios = [];
                                const subsLocais = compLocal.subcriterios;

                                compAtualizada.subcriterios.forEach((scAtualizado) => {
                                    if (!scAtualizado?.id) return;
                                    let scLocal = subsLocais.find(s => s?.id === scAtualizado.id);
                                    if (!scLocal) {
                                        subsLocais.push(scAtualizado);
                                        return;
                                    }

                                    if (typeof scAtualizado.nota === 'number') scLocal.nota = scAtualizado.nota;
                                    if (typeof scAtualizado.intervalo_nota === 'number') scLocal.intervalo_nota = scAtualizado.intervalo_nota;
                                    if (typeof scAtualizado.nota_maxima === 'number') scLocal.nota_maxima = scAtualizado.nota_maxima;
                                    if (scAtualizado.updated_at) scLocal.updated_at = scAtualizado.updated_at;
                                    if (Array.isArray(scAtualizado.comentarios)) scLocal.comentarios = scAtualizado.comentarios;
                                });
                            }
                        });
                    }
                }

                return response;
            } catch (error) {
                this.error = 'Erro ao salvar notas da avaliação';
                console.error('Erro ao salvar notas da avaliação:', error);
                throw error;
            }
        },

        /**
         * Salva uma avaliação individual de competência (criação ou edição)
         * @param {Object} dados - Dados da avaliação
         * @param {number} dados.documento_id - ID do documento
         * @param {number} dados.competencia_id - ID da competência
         * @param {number|null} dados.correcao_id - ID da correção (null se não tiver correção)
         * @param {number|null} dados.avaliacao_id - ID da avaliação (null para criação, número para edição)
         * @param {number|null} dados.avaliacao_redacao_id - ID da avaliação redação (null para criação, número para edição)
         * @param {number|null} dados.comentario_id - ID do comentário (null para criação, número para edição)
         * @param {number} dados.nota - Nota da avaliação
         * @param {string} dados.comentario - Comentário do risco
         * @param {string} dados.pathJSON - JSON do path do risco no canvas
         * @param {boolean} dados.isHighlight - Se é highlight ou risco
         * @param {number} dados.originalCanvasWidth - Largura original do canvas
         * @param {number} dados.originalCanvasHeight - Altura original do canvas
         * @param {string} dados.pathId - ID único do path no canvas
         * @returns {Promise<Object>} Resposta da API com a avaliação salva
         */
        async salvarAvaliacaoIndividual(dados) {
            this.error = null;
            try {
                const payload = {
                    documento_id: dados.documento_id,
                    competencia_id: dados.competencia_id ?? null,
                    correcao_id: dados.correcao_id || null,
                    nota: Number(dados.nota || 0),
                    comentario: dados.comentario || '',
                    path: dados.pathJSON || dados.path || null, // Aceita pathJSON (canvas) ou path (texto com offsets)
                    original_canvas_width: dados.originalCanvasWidth,
                    original_canvas_height: dados.originalCanvasHeight,
                    path_id: dados.pathId
                };

                // Só enviar is_highlight quando o chamador definiu explicitamente (evita false por omissão no PUT).
                if (
                    Object.prototype.hasOwnProperty.call(dados, 'isHighlight') ||
                    Object.prototype.hasOwnProperty.call(dados, 'is_highlight')
                ) {
                    payload.is_highlight = !!(dados.isHighlight ?? dados.is_highlight);
                }

                // Comentário sem competência (seção "Outros comentários")
                if (dados.sem_competencia === true) {
                    payload.sem_competencia = true;
                }

                // Cor personalizada da marcação (hex: #RRGGBB ou #RGB)
                if (dados.cor !== undefined) {
                    payload.cor = dados.cor;
                }

                // Suporte a subcritérios (opcional)
                if (dados.subcriterio_id) {
                    payload.subcriterio_id = dados.subcriterio_id;
                }

                // Adicionar tipo se fornecido (para diferenciar entre canvas e texto)
                if (dados.tipo) {
                    payload.tipo = dados.tipo;
                }

                // Se tiver avaliacao_id, é edição (PUT), adicionar avaliacao_redacao_id e comentario_id
                if (dados.avaliacao_id) {
                    if (dados.avaliacao_redacao_id) {
                        payload.avaliacao_redacao_id = dados.avaliacao_redacao_id;
                    }
                }
                
                // Adicionar comentario_id se existir (tanto para criação quanto edição)
                if (dados.comentario_id) {
                    payload.comentario_id = dados.comentario_id;
                }

                // Se tiver avaliacao_id, é edição (PUT), senão é criação (POST)
                let response;
                if (dados.avaliacao_id) {
                    // Edição - atualizar avaliação existente
                    response = await axios.put(
                        `/api/v1/documento/${dados.documento_id}/comentario/${dados.avaliacao_id}`,
                        payload
                    );
                } else {
                    // Criação - criar nova avaliação
                    response = await axios.post(
                        `/api/v1/documento/${dados.documento_id}/comentario`,
                        payload
                    );
                }

                return response;
            } catch (error) {
                this.error = 'Erro ao salvar avaliação';
                console.error('Erro ao salvar avaliação:', error);
                throw error;
            }
        },

        /**
         * Obtém um comentário específico por ID
         * @param {string} documentoId - ID do documento
         * @param {string} comentarioId - ID do comentário a ser obtido
         * @returns {Promise<Object>} Resposta da API com os dados do comentário (inclui campo cor)
         */
        async obterComentario(documentoId, comentarioId) {
            this.error = null;
            try {
                const response = await axios.get(
                    `/api/v1/documento/${documentoId}/comentario/${comentarioId}`
                );
                return response.data;
            } catch (error) {
                this.error = 'Erro ao obter comentário';
                console.error('Erro ao obter comentário:', error);
                throw error;
            }
        },

        /**
         * Atualiza um comentário pelo ID (funciona para comentários com ou sem competência)
         * PUT /documento/{id}/comentario-item/{comentario_id}
         * @param {string} documentoId - ID do documento
         * @param {string} comentarioId - ID do comentário
         * @param {Object} dados - { comentario?, path?, is_highlight?, tipo?, cor? }
         * @returns {Promise<Object>} Resposta da API
         */
        async atualizarComentarioPorId(documentoId, comentarioId, dados) {
            this.error = null;
            try {
                const payload = {};
                if (dados.comentario !== undefined) payload.comentario = dados.comentario;
                if (dados.path !== undefined) payload.path = dados.path;
                if (dados.is_highlight !== undefined) payload.is_highlight = dados.is_highlight;
                if (dados.tipo !== undefined) payload.tipo = dados.tipo;
                if (dados.cor !== undefined) payload.cor = dados.cor;
                const response = await axios.put(
                    `/api/v1/documento/${documentoId}/comentario-item/${comentarioId}`,
                    payload
                );
                return response;
            } catch (error) {
                this.error = 'Erro ao atualizar comentário';
                console.error('Erro ao atualizar comentário:', error);
                throw error;
            }
        },

        /**
         * Deleta um comentário individual
         * @param {string} documentoId - ID do documento
         * @param {string} comentarioId - ID do comentário a ser deletado
         * @returns {Promise<Object>} Resposta da API
         */
        async deletarComentario(documentoId, comentarioId) {
            this.error = null;
            try {
                const response = await axios.delete(
                    `/api/v1/documento/${documentoId}/comentario/${comentarioId}`
                );
                return response;
            } catch (error) {
                this.error = 'Erro ao deletar comentário';
                console.error('Erro ao deletar comentário:', error);
                throw error;
            }
        },

        /**
         * Deleta uma correção completa
         * @param {string} documentoId - ID do documento
         * @param {string} correcaoId - ID da correção a ser deletada
         * @returns {Promise<Object>} Resposta da API
         */
        async deletarCorrecao(documentoId, correcaoId) {
            this.error = null;
            try {
                const response = await axios.delete(
                    `/api/v1/documento/${documentoId}/correcao/${correcaoId}/`
                );
                return response;
            } catch (error) {
                this.error = 'Erro ao deletar correção';
                console.error('Erro ao deletar correção:', error);
                throw error;
            }
        },

        async iniciarCorrecaoAutomatica(
            documentoId,
            promptId,
            revisarTranscricao = false,
            gerarAudio = undefined,
            enviarParaRevisao = undefined
        ) {
            this.error = null;
            const body = {
                prompt_id: promptId,
                revisar_transcricao: revisarTranscricao,
            };
            if (gerarAudio !== undefined) {
                body.gerar_audio = gerarAudio;
            }
            if (enviarParaRevisao !== undefined) {
                body.enviar_para_revisao = enviarParaRevisao;
            }
            const url = `/api/v1/documento/${documentoId}/iniciar-correcao-automatica`;
            try {
                return await executeWithPlanOverageConsentRetry(
                    () => axios.post(url, body),
                    'correcoes_ia'
                );
            } catch (error) {
                this.error = 'Erro ao iniciar correção automática';
                console.error('Erro ao iniciar correção automática:', error);
                throw error;
            }
        },

        async fetchSugestoesComentario(documentoId, payload, options = {}) {
            this.error = null;
            try {
                const response = await axios.post(
                    `/api/v1/documento/${documentoId}/sugestoes-comentario`,
                    payload,
                    { signal: options.signal }
                );
                return response.data;
            } catch (error) {
                if (error?.code !== 'ERR_CANCELED' && error?.name !== 'CanceledError') {
                    this.error = 'Erro ao buscar sugestões de comentário';
                    console.error('Erro ao buscar sugestões de comentário:', error);
                }
                throw error;
            }
        },

        async iniciarMarcacoesIaPersonalizadas(documentoId, payload) {
            this.error = null;
            const url = `/api/v1/documento/${documentoId}/iniciar-marcacoes-ia-personalizadas`;
            try {
                return await executeWithPlanOverageConsentRetry(
                    () => axios.post(url, payload),
                    'correcoes_ia'
                );
            } catch (error) {
                this.error = 'Erro ao iniciar marcações personalizadas';
                console.error('Erro ao iniciar marcações personalizadas:', error);
                throw error;
            }
        },

        async criarCorrecaoManual(documentoId) {
            this.error = null;
            try {
                const response = await axios.post(`/api/v1/documento/${documentoId}/criar-correcao-manual`);

                let correcao = null;
                if (response.data?.data?.correcao) {
                    correcao = response.data.data.correcao;
                } else if (response.data?.correcao) {
                    correcao = response.data.correcao;
                } else if (response.data?.data) {
                    correcao = response.data.data;
                } else if (response.data) {
                    correcao = response.data;
                }

                if (correcao && this.documento?.id === documentoId) {
                    if (!this.documento.correcoes) {
                        this.documento.correcoes = [];
                    }

                    normalizeCorrecaoAvaliacoesAliases(correcao);

                    const correcaoIndex = this.documento.correcoes.findIndex(c => c.id === correcao.id);
                    if (correcaoIndex === -1) {
                        this.documento.correcoes.push(correcao);
                    } else {
                        this.documento.correcoes[correcaoIndex] = {
                            ...this.documento.correcoes[correcaoIndex],
                            ...correcao
                        };
                    }
                }

                return correcao;
            } catch (error) {
                this.error = 'Erro ao criar correção manual';
                console.error('Erro ao criar correção manual:', error);
                throw error;
            }
        },

        async enviarCorrecao() {
            this.error = null;
            try {
                const formData = new FormData();
                formData.append('id', this.documento.id);
                
                // Enviar correção escrita (quando houver). O `preview.vue` seta `documento.correcao`
                // antes de chamar este método, então precisamos incluir no payload.
                if (this.documento.correcao !== undefined && this.documento.correcao !== null) {
                    formData.append('correcao', this.documento.correcao);
                }
                
                // Apenas gerenciar o áudio - comentários e avaliações já são salvos automaticamente
                if (this.documento.correcao_midia !== undefined && this.documento.correcao_midia !== null) {
                    formData.append('correcao_midia', this.documento.correcao_midia);
                } else if (this.documento.correcoes && this.documento.correcoes.length > 0 && 
                          this.documento.correcoes[0].midia_file_path && !this.audioRemovido) {
                    formData.append('manter_audio_anterior', 'true');
                }
                
                const response = await axios.post(
                    `/api/v1/documento/${this.documento.id}/enviar-correcao`, 
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                
                if (response?.data?.data) {
                    this.documento = response.data.data;
                }

                return response;
            } catch (error) {
                this.error = 'Erro ao enviar correção'; 
                console.error('Erro ao enviar correção:', error);
                throw error;
            }
        },

        /**
         * Persiste áudio da correção via fluxo novo de feedback_audios (attachable Correcao).
         */
        async salvarAudioCorrecao(arquivo, nome = null, correcaoId = null) {
            this.error = null;
            const correcaoIdEnviado = correcaoId != null && String(correcaoId).trim() !== '' ? String(correcaoId).trim() : null;
            if (!arquivo || !(arquivo instanceof File)) {
                const err = new Error('Arquivo de áudio inválido');
                this.error = err.message;
                throw err;
            }
            if (!this.documento?.id) {
                const err = new Error('Documento não carregado');
                this.error = err.message;
                throw err;
            }
            try {
                const attachableId = correcaoIdEnviado || this.documento?.correcoes?.[0]?.id || null;
                if (!attachableId) {
                    const err = new Error('Nenhuma correção alvo encontrada para anexar áudio');
                    this.error = err.message;
                    throw err;
                }
                const audio = await this.uploadFeedbackAudio(String(this.documento.id), {
                    attachableType: FEEDBACK_AUDIO_CORRECAO,
                    attachableId: String(attachableId),
                    file: arquivo,
                    nome: nome && String(nome).trim() ? String(nome).trim() : null,
                });
                const correcao = (this.documento?.correcoes || []).find(c => String(c.id) === String(attachableId)) || null;
                this.documento.correcao_midia = null;
                return {
                    correcao,
                    feedback_audio: audio || null,
                    midia_file_path: audio?.file_path || null,
                    midia_file_mime_type: audio?.file_mime_type || null,
                    midia_file_nome: audio?.file_nome || null,
                };
            } catch (error) {
                this.error = error.response?.data?.message || 'Erro ao salvar áudio da correção';
                console.error('Erro ao salvar áudio da correção:', error);
                throw error;
            }
        },

        /**
         * POST /documento/{id}/feedback-audio — anexa áudio a correção, avaliação ou comentário.
         */
        async uploadFeedbackAudio(documentoId, { attachableType, attachableId, file, nome = null }) {
            this.error = null;
            if (!file || !(file instanceof File)) {
                const err = new Error('Ficheiro de áudio inválido');
                this.error = err.message;
                throw err;
            }
            if (!isValidDocumentoApiId(documentoId) || !attachableType || !attachableId) {
                const err = new Error('Dados em falta para anexar áudio');
                this.error = err.message;
                throw err;
            }
            try {
                const formData = new FormData();
                formData.append('attachable_type', attachableType);
                formData.append('attachable_id', String(attachableId));
                formData.append('audio', file);
                if (nome && String(nome).trim()) formData.append('nome', String(nome).trim());
                const response = await axios.post(
                    `/api/v1/documento/${documentoId}/feedback-audio`,
                    formData,
                    {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        maxContentLength: 50 * 1024 * 1024,
                        maxBodyLength: 50 * 1024 * 1024,
                    }
                );
                const audio = response?.data?.data?.feedback_audio;
                if (audio && this.documento?.id === documentoId) {
                    this._mergeFeedbackAudioIntoLocalDocumento(attachableType, String(attachableId), audio);
                }
                return audio;
            } catch (error) {
                this.error = error.response?.data?.message || 'Erro ao anexar áudio';
                throw error;
            }
        },

        async deleteFeedbackAudio(documentoId, feedbackAudioId) {
            this.error = null;
            if (!isValidDocumentoApiId(documentoId) || !feedbackAudioId) {
                const err = new Error('Identificador inválido');
                this.error = err.message;
                throw err;
            }
            try {
                await axios.delete(`/api/v1/documento/${documentoId}/feedback-audio/${feedbackAudioId}`);
                if (this.documento?.id === documentoId) {
                    this._removeFeedbackAudioFromLocalDocumento(String(feedbackAudioId));
                }
            } catch (error) {
                this.error = error.response?.data?.message || 'Erro ao remover áudio';
                throw error;
            }
        },

        _mergeFeedbackAudioIntoLocalDocumento(attachableType, attachableId, audio) {
            const correcoes = this.documento?.correcoes;
            const t = String(attachableType);
            const id = String(attachableId);
            if (Array.isArray(correcoes)) {
                for (const c of correcoes) {
                    if (t.endsWith('Correcao') && String(c.id) === id) {
                        if (!Array.isArray(c.feedback_audios)) c.feedback_audios = [];
                        c.feedback_audios.push(audio);
                        return;
                    }
                    const avs = c.avaliacoes_redacao || c.avaliacoesRedacao;
                    if (!Array.isArray(avs)) continue;
                    for (const av of avs) {
                        if (t.includes('AvaliacaoRedacao') && String(av.id) === id) {
                            if (!Array.isArray(av.feedback_audios)) av.feedback_audios = [];
                            av.feedback_audios.push(audio);
                            return;
                        }
                        const coms = av.comentarios || [];
                        for (const com of coms) {
                            if (t.includes('Comentario') && String(com.id) === id) {
                                if (!Array.isArray(com.feedback_audios)) com.feedback_audios = [];
                                com.feedback_audios.push(audio);
                                return;
                            }
                        }
                    }
                    const comsC = c.comentarios || [];
                    for (const com of comsC) {
                        if (t.includes('Comentario') && String(com.id) === id) {
                            if (!Array.isArray(com.feedback_audios)) com.feedback_audios = [];
                            com.feedback_audios.push(audio);
                            return;
                        }
                    }
                }
            }

            if (t.includes('Comentario')) {
                const competencias = this.documento?.competencias || [];
                for (const comp of competencias) {
                    const coms = comp?.comentarios || [];
                    for (const com of coms) {
                        if (String(com?.id) === id) {
                            if (!Array.isArray(com.feedback_audios)) com.feedback_audios = [];
                            com.feedback_audios.push(audio);
                            return;
                        }
                    }
                    const subcriterios = comp?.subcriterios || [];
                    for (const sc of subcriterios) {
                        const scComs = sc?.comentarios || [];
                        for (const com of scComs) {
                            if (String(com?.id) === id) {
                                if (!Array.isArray(com.feedback_audios)) com.feedback_audios = [];
                                com.feedback_audios.push(audio);
                                return;
                            }
                        }
                    }
                }
            }
        },

        _removeFeedbackAudioFromLocalDocumento(feedbackAudioId) {
            const correcoes = this.documento?.correcoes;
            const fid = String(feedbackAudioId);
            const strip = (arr) => {
                if (!Array.isArray(arr)) return;
                const idx = arr.findIndex((x) => String(x.id) === fid);
                if (idx >= 0) arr.splice(idx, 1);
            };
            if (Array.isArray(correcoes)) {
                for (const c of correcoes) {
                    strip(c.feedback_audios);
                    const avs = c.avaliacoes_redacao || c.avaliacoesRedacao;
                    if (!Array.isArray(avs)) continue;
                    for (const av of avs) {
                        strip(av.feedback_audios);
                        const coms = av.comentarios || [];
                        for (const com of coms) {
                            strip(com.feedback_audios);
                        }
                    }
                    const comsC = c.comentarios || [];
                    for (const com of comsC) {
                        strip(com.feedback_audios);
                    }
                }
            }

            const competencias = this.documento?.competencias || [];
            for (const comp of competencias) {
                const coms = comp?.comentarios || [];
                for (const com of coms) {
                    strip(com.feedback_audios);
                }
                const subcriterios = comp?.subcriterios || [];
                for (const sc of subcriterios) {
                    const scComs = sc?.comentarios || [];
                    for (const com of scComs) {
                        strip(com.feedback_audios);
                    }
                }
            }
        },

        async obterDocumentoPdf(documentoId) {
            if (!isValidDocumentoApiId(documentoId)) return;
            this.error = null;
            try {
                const response = await axios.get(`/api/v1/documento/pdf/${documentoId}`);
                if (response.data?.data) {
                    this.pdfUrl = response.data.data;
                }
            } catch (error) {
                this.error = 'Erro ao obter PDF do documento';
                console.error('Erro ao obter PDF:', error);
                throw error;
            }
        },

        async obtemUltimoRascunho(setDocumento = false) {
            this.error = null;
            try {
                const response = await axios.get(`/api/v1/documento-rascunho`);
                if (response.status === 200 && response.data.data) {
                    if (setDocumento) {
                        this.documento = response.data.data;
                    } else {
                        this.temRascunho = true;
                    }
                } else {
                    this.temRascunho = false;
                }
            } catch (error) {
                this.error = 'Erro ao obter rascunho';
                console.error('Erro ao obter rascunho:', error);
                throw error;
            }
        },

        limpar() {
            this.documento = {
                id: null,
                tipo: '',
                parametros: [],
                correcao: '',
                correcao_midia: null,
                correcao_file: null,
                competencias: [],
                comentarios: [],
                pathComments: [],
                canvasHistory: [],
                isDrawingMode: false,
                isHighlightMode: false,
                isImage: false,
                documentSrc: '',
                pdfSrc: '',
                numOfPages: 0,
                pdfUrl: '',
                audioRemovido: false,
                rotatingImage: false,
                imageRotationError: null,
                path: null,
                path_parte_inferior: null,
                texto_redacao: '',
                paciente: {
                    user: {
                        pessoal_info: {},
                        endereco: {}
                    }
                }
            };
            this.pdfUrl = '';
            this.error = null;
            this.audioRemovido = false;
            this.rotatingImage = false;
            this.imageRotationError = null;
        },

        limparPaciente() {
            this.documento.paciente = {
                user: {
                    pessoal_info: {},
                    endereco: {}
                }
            };
        },

        addItem(item) {
            this.items.push(item);
        },

        removeItem(index) {
            this.items.splice(index, 1);
        },

        updateItems(newItems) {
            this.items = newItems;
        },

        /**
         * Rotaciona a imagem do documento
         * @param {number} direcao - Direção da rotação (90, 180, 270)
         * @returns {Promise<Object>} Resposta da API
         */
        async rotacionarImagem(direcao) {
            if (!this.documento?.id) {
                throw new Error('ID do documento não encontrado');
            }

            // Validação da direção
            const direcoesValidas = [90, 180, 270];
            if (!direcoesValidas.includes(direcao)) {
                throw new Error('Direção inválida. Use 90, 180 ou 270 graus');
            }

            this.rotatingImage = true;
            this.imageRotationError = null;

            try {
                const response = await axios.post(
                    `/api/v1/documento/${this.documento.id}/girar-imagem`,
                    { direcao },
                    {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                );

                // Verificar se a resposta foi bem-sucedida
                if (response.data?.status === 'success' && response.data?.data?.nova_url) {
                    // Atualiza a URL da imagem no documento
                    if (this.documento.correcao_file) {
                        this.documento.correcao_file = response.data.data.nova_url;
                    }
                    
                    return {
                        success: true,
                        data: response.data.data,
                        message: response.data.message || 'Imagem girada com sucesso'
                    };
                } else {
                    throw new Error(response.data?.message || 'Erro ao girar imagem');
                }

            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || 'Erro ao girar imagem';
                this.imageRotationError = errorMessage;
                throw new Error(errorMessage);
            } finally {
                this.rotatingImage = false;
            }
        },

        /**
         * Limpa o erro de rotação de imagem
         */
        limparErroRotacao() {
            this.imageRotationError = null;
        },

        /**
         * Baixa a redação em PDF
         * @param {number} documentoId - ID do documento
         * @param {FormData} formData - FormData opcional com imagem (usado no preview)
         * @param {Object} opcoes - Opções adicionais
         * @param {boolean} opcoes.comImagem - Se deve incluir imagem no FormData (padrão: false)
         * @param {string} opcoes.correcaoUuid - UUID da correção (opcional)
         * @param {boolean} opcoes.mostrarConfirmacao - Se deve mostrar confirmação antes (padrão: true)
         * @param {Array} opcoes.selectedCorrecoesIds - IDs das correções selecionadas (opcional)
         * @param {Record<string, boolean>} [opcoes.opcoesGeracao] — ver `PDF_GERACAO_OPCOES_KEYS` em `@/constants/pdfGeracaoOpcoes` (omitir = backend usa padrão true)
         * @returns {Promise<void>}
         */
        async baixarRedacaoPdf(documentoId, formData = null, opcoes = {}) {
            const {
                comImagem = false,
                correcaoUuid = null,
                mostrarConfirmacao = true,
                selectedCorrecoesIds = [],
                opcoesGeracao,
            } = opcoes;
            
            // Mostrar confirmação se solicitado
            if (mostrarConfirmacao) {
                const result = await window.Swal.fire({
                    title: 'Baixar redação?',
                    text: 'Deseja baixar a redação em PDF?',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Baixar',
                    cancelButtonText: 'Cancelar',
                    padding: '2em'
                });

                if (!result.isConfirmed) {
                    return;
                }
            }

            // Mostrar SweetAlert de loading
            window.Swal.fire({
                title: 'Gerando documento...',
                text: 'Por favor, aguarde enquanto o PDF está sendo gerado.',
                icon: 'info',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    window.Swal.showLoading();
                }
            });

            try {
                // Preparar FormData se necessário
                let requestData;
                let config = {
                    responseType: 'blob',
                    timeout: 120000, // 2 minutos
                    maxContentLength: Infinity,
                    maxBodyLength: Infinity
                };

                if (comImagem && formData) {
                    // Caso do preview - com imagem
                    requestData = formData;
                    
                    // Adicionar IDs das correções selecionadas se houver
                    if (selectedCorrecoesIds && selectedCorrecoesIds.length > 0) {
                        requestData.append('selected_correcoes_ids', JSON.stringify(selectedCorrecoesIds));
                    }

                    appendPdfGeracaoOpcoesToFormData(requestData, opcoesGeracao);
                    
                    config.headers = {
                        'Content-Type': 'multipart/form-data'
                    };
                } else {
                    // Caso da lista - apenas documento ID
                    requestData = new FormData();
                    if (correcaoUuid) {
                        requestData.append('correcao_uuid', correcaoUuid);
                    }
                    
                    // Adicionar IDs das correções selecionadas se houver
                    if (selectedCorrecoesIds && selectedCorrecoesIds.length > 0) {
                        requestData.append('selected_correcoes_ids', JSON.stringify(selectedCorrecoesIds));
                    }

                    appendPdfGeracaoOpcoesToFormData(requestData, opcoesGeracao);
                    
                    config.headers = {
                        'Content-Type': 'multipart/form-data'
                    };
                }

                // Fazer requisição
                const response = await axios.post(
                    `/api/v1/documento/${documentoId}/gerar-pdf-correcao`,
                    requestData,
                    config
                );

                // Fechar SweetAlert de loading
                window.Swal.close();

                // Criar blob e URL para download (não abrir em nova guia)
                const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
                const pdfUrl = window.URL.createObjectURL(pdfBlob);

                // Nome do arquivo: Content-Disposition do backend ou "Correção - Aluno - Turma - Tema.pdf"
                const contentDisposition = response.headers['content-disposition'];
                const nomeAluno = this.documento?.aluno?.name || this.documento?.paciente?.user?.name || this.documento?.paciente?.nome || 'Aluno';
                const nomeTurma = this.documento?.turma?.nome || this.documento?.turma?.name || this.documento?.turma?.titulo || 'Turma';
                const nomeTema = this.documento?.tema?.titulo || this.documento?.tema?.nome || 'Tema';
                const sanitize = (s) => String(s || '').replace(/[/\\:*?"<>|]/g, '-').trim() || 'Sem nome';
                const nomePadrao = `Correção - ${sanitize(nomeAluno)} - ${sanitize(nomeTurma)} - ${sanitize(nomeTema)}.pdf`;

                let filename = nomePadrao;
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i)
                        || contentDisposition.match(/filename=["']?([^"';\n]+)["']?/i);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '').trim();
                        if (filename.includes("''")) {
                            filename = decodeURIComponent(filename.split("''")[1] || filename);
                        }
                    }
                }
                if (!filename.toLowerCase().endsWith('.pdf')) {
                    filename += '.pdf';
                }

                const link = document.createElement('a');
                link.href = pdfUrl;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(pdfUrl);

            } catch (error) {
                console.error('Erro ao baixar PDF:', error);
                
                // Fechar SweetAlert de loading
                window.Swal.close();
                
                // Determinar mensagem de erro
                let errorMessage = 'Erro ao gerar PDF da redação. Tente novamente.';
                
                if (error.code === 'ECONNABORTED') {
                    errorMessage = 'Timeout: O processo está demorando mais que o esperado. Tente novamente.';
                } else if (error.response?.status === 413) {
                    errorMessage = 'Arquivo muito grande. Tente novamente.';
                } else if (error.response?.status === 422) {
                    errorMessage = 'Dados inválidos. Verifique se a correção está disponível.';
                } else if (error.response?.status === 500) {
                    errorMessage = 'Erro interno do servidor. Tente novamente em alguns minutos.';
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                }

                // Mostrar erro
                window.Swal.fire({
                    title: 'Erro!',
                    text: errorMessage,
                    icon: 'error',
                    padding: '2em'
                });

                throw error;
            }
        },

        /**
         * Gera PDF em lote para múltiplos documentos
         * @param {Array<string>} documentoUuids - Array de UUIDs dos documentos
         * @param {Record<string, boolean>} [opcoesGeracao] — opções de seções (omitir = todas true no backend)
         * @param {string|null|undefined} [email] — e-mail opcional para envio do link do PDF (RFC); omitir/null = e-mail do usuário autenticado
         * @returns {Promise<Object>} Resposta da API
         */
        async gerarPdfLote(documentoUuids, opcoesGeracao, email) {
            this.error = null;
            try {
                const body = {
                    documento_uuids: documentoUuids,
                    ...buildPdfGeracaoOpcoesJsonBody(opcoesGeracao),
                };
                const emailTrim =
                    typeof email === 'string' ? email.trim() : '';
                if (emailTrim) {
                    body.email = emailTrim;
                }

                const response = await axios.post('/api/v1/exportacao-pdf/gerar', body);
                
                return response;
            } catch (error) {
                this.error = 'Erro ao gerar PDF em lote';
                console.error('Erro ao gerar PDF em lote:', error);
                
                let errorMessage = 'Erro ao gerar PDF em lote. Tente novamente.';
                
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                }
                
                throw new Error(errorMessage);
            }
        },

        /**
         * PDF consolidado de toda a turma (documentos com pelo menos uma correção).
         * POST /exportacao-pdf/turmas/{turma}/consolidado
         * @param {string} turmaUuid - UUID da turma (model binding)
         * @param {Record<string, boolean>} [opcoesGeracao]
         * @param {string|null|undefined} [email]
         * @returns {Promise<Object>} Resposta da API (201)
         */
        async gerarPdfConsolidadoPorTurma(turmaUuid, opcoesGeracao, email) {
            this.error = null;
            if (!turmaUuid) {
                throw new Error('UUID da turma não informado');
            }
            try {
                const body = {
                    ...buildPdfGeracaoOpcoesJsonBody(opcoesGeracao),
                };
                const emailTrim =
                    typeof email === 'string' ? email.trim() : '';
                if (emailTrim) {
                    body.email = emailTrim;
                }

                const response = await axios.post(
                    `/api/v1/exportacao-pdf/turmas/${encodeURIComponent(turmaUuid)}/consolidado`,
                    body,
                );

                return response;
            } catch (error) {
                this.error = 'Erro ao solicitar PDF consolidado da turma';
                console.error('Erro ao solicitar PDF consolidado da turma:', error);

                let errorMessage =
                    'Erro ao solicitar PDF consolidado da turma. Tente novamente.';

                if (error.response?.status === 403) {
                    errorMessage =
                        'Você não tem permissão para exportar o PDF desta turma.';
                } else if (error.response?.status === 404) {
                    errorMessage =
                        'Turma não encontrada ou não há documentos com correção para consolidar.';
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                }

                throw new Error(errorMessage);
            }
        },

        /**
         * Consulta uma exportação PDF (inclui opcoes_geracao efetivas quando a API retornar).
         * @param {string} exportacaoId - UUID ou id da exportação
         * @returns {Promise<Object>} Resposta axios (data conforme API)
         */
        async obterExportacaoPdf(exportacaoId) {
            this.error = null;
            try {
                return await axios.get(`/api/v1/exportacao-pdf/${exportacaoId}`);
            } catch (error) {
                this.error = 'Erro ao consultar exportação PDF';
                console.error('Erro ao consultar exportação PDF:', error);
                throw error;
            }
        },

        /**
         * Atualiza o status de um documento
         * @param {string} documentoId - UUID do documento
         * @param {string} status - Novo status (pendente, corrigindo, corrigido, revisao)
         * @param {string} motivo - Motivo da alteração do status
         * @returns {Promise<Object>} Resposta da API
         */
        async atualizarStatus(documentoId, status, motivo) {
            this.error = null;
            try {
                const response = await axios.patch(
                    `/api/v1/documento/${documentoId}/update-status`,
                    {
                        status,
                        motivo
                    }
                );
                
                // Atualiza o documento local se for o documento atual
                if (this.documento?.id === documentoId && response.data?.data) {
                    this.documento.status = response.data.data.novo_status;
                }
                
                return response;
            } catch (error) {
                this.error = 'Erro ao atualizar status do documento';
                console.error('Erro ao atualizar status:', error);
                
                let errorMessage = 'Erro ao atualizar status do documento. Tente novamente.';
                
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                }
                
                throw new Error(errorMessage);
            }
        },

        /**
         * Obtém uma correção específica por ID
         * @param {string} documentoId - UUID do documento
         * @param {string} correcaoId - UUID da correção
         * @returns {Promise<Object>} Dados da correção
         */
        async obterCorrecao(documentoId, correcaoId) {
            this.error = null;
            try {
                const response = await axios.get(
                    `/api/v1/documento/${documentoId}/correcao/${correcaoId}`
                );
                
                // A resposta segue a estrutura: { success: true, message: "...", data: { correcao: {...} } }
                let correcao = null;
                if (response.data?.data?.correcao) {
                    correcao = response.data.data.correcao;
                } else if (response.data?.data) {
                    // Fallback: se data for a própria correção
                    correcao = response.data.data;
                } else if (response.data?.correcao) {
                    // Fallback: se correcao estiver diretamente em data
                    correcao = response.data.correcao;
                } else if (response.data) {
                    // Último fallback: usar data diretamente
                    correcao = response.data;
                }
                
                if (!correcao) {
                    console.warn('⚠️ Resposta da API não contém dados da correção na estrutura esperada');
                    console.warn('⚠️ Resposta recebida:', response.data);
                    throw new Error('Correção não encontrada na resposta da API');
                }
                
                // Se a correção foi encontrada e é do documento atual, adicionar/atualizar na lista
                if (correcao && this.documento?.id === documentoId) {
                    if (!this.documento.correcoes) {
                        this.documento.correcoes = [];
                    }
                    
                    const correcaoIndex = this.documento.correcoes.findIndex(c => c.id === correcaoId);
                    if (correcaoIndex === -1) {
                        // CRÍTICO: Garantir compatibilidade entre camelCase e snake_case
                        // A API retorna avaliacoesRedacao (camelCase), mas o frontend pode usar avaliacoes_redacao (snake_case)
                        if (correcao.avaliacoesRedacao && !correcao.avaliacoes_redacao) {
                            correcao.avaliacoes_redacao = correcao.avaliacoesRedacao;
                        } else if (correcao.avaliacoes_redacao && !correcao.avaliacoesRedacao) {
                            correcao.avaliacoesRedacao = correcao.avaliacoes_redacao;
                        }
                        
                        // Adicionar nova correção
                        this.documento.correcoes.push(correcao);
                        
                        // Log detalhado sobre avaliações e comentários
                        const avaliacoes = correcao.avaliacoesRedacao || correcao.avaliacoes_redacao || [];
                        let totalComentarios = 0;
                        avaliacoes.forEach(av => {
                            if (Array.isArray(av.comentarios)) {
                                totalComentarios += av.comentarios.length;
                            }
                        });
                        
                        console.log('✅ Nova correção adicionada ao documento:', correcaoId, {
                            temAvaliacoes: avaliacoes.length > 0,
                            totalAvaliacoes: avaliacoes.length,
                            totalComentarios: totalComentarios,
                            userId: correcao.user_id,
                            temUser: !!correcao.user,
                            temAvaliacoesRedacao: !!correcao.avaliacoesRedacao,
                            temAvaliacoes_redacao: !!correcao.avaliacoes_redacao,
                            avaliacoesDetalhes: avaliacoes.map(av => ({
                                id: av.id,
                                competencia_id: av.competencia_id,
                                totalComentarios: Array.isArray(av.comentarios) ? av.comentarios.length : 0,
                                comentariosIds: Array.isArray(av.comentarios) ? av.comentarios.map(c => c?.id).filter(Boolean) : []
                            }))
                        });
                    } else {
                        // Atualizar correção existente
                        // CRÍTICO: Preservar avaliacoes_redacao existentes se a correção já tiver
                        const avaliacoesExistentes = this.documento.correcoes[correcaoIndex].avaliacoes_redacao || 
                                                      this.documento.correcoes[correcaoIndex].avaliacoesRedacao;
                        
                        // A API retorna avaliacoesRedacao (camelCase), mas o frontend pode usar avaliacoes_redacao (snake_case)
                        const avaliacoesNovas = correcao.avaliacoesRedacao || correcao.avaliacoes_redacao;
                        
                        // Mesclar avaliações: manter existentes e adicionar novas
                        if (avaliacoesExistentes && avaliacoesNovas) {
                            const avaliacoesMescladas = [...avaliacoesExistentes];
                            avaliacoesNovas.forEach(avaliacaoNova => {
                                const indexExistente = avaliacoesMescladas.findIndex(a => a.id === avaliacaoNova.id);
                                if (indexExistente === -1) {
                                    avaliacoesMescladas.push(avaliacaoNova);
                                } else {
                                    avaliacoesMescladas[indexExistente] = {
                                        ...avaliacoesMescladas[indexExistente],
                                        ...avaliacaoNova
                                    };
                                }
                            });
                            correcao.avaliacoes_redacao = avaliacoesMescladas;
                            correcao.avaliacoesRedacao = avaliacoesMescladas;
                        }
                        
                        this.documento.correcoes[correcaoIndex] = {
                            ...this.documento.correcoes[correcaoIndex],
                            ...correcao
                        };
                        console.log('✅ Correção atualizada no documento:', correcaoId, {
                            temAvaliacoes: !!correcao.avaliacoesRedacao,
                            totalAvaliacoes: correcao.avaliacoesRedacao?.length || 0,
                            avaliacoesExistentes: avaliacoesExistentes?.length || 0,
                            avaliacoesNovas: avaliacoesNovas?.length || 0
                        });
                    }
                }
                
                return correcao;
            } catch (error) {
                this.error = 'Erro ao obter correção';
                console.error('❌ Erro ao obter correção:', error);
                console.error('❌ Status do erro:', error.response?.status);
                console.error('❌ Dados do erro:', error.response?.data || error.message);
                
                // Se for 404, não é um erro crítico - a correção simplesmente não existe
                if (error.response?.status === 404) {
                    console.warn('⚠️ Correção não encontrada (404). Pode ser que ainda não tenha sido criada.');
                    return null;
                }
                
                throw error;
            }
        },

        /**
         * Atualiza a correção escrita de uma correção específica
         * @param {string} documentoId - UUID do documento
         * @param {string} correcaoId - UUID da correção
         * @param {string} novoTexto - Novo texto da correção escrita
         * @returns {Promise<Object>} Resposta da API
         */
        async atualizarCorrecaoEscrita(documentoId, correcaoId, novoTexto) {
            this.error = null;
            try {
                const response = await axios.patch(
                    `/api/v1/documento/${documentoId}/correcao/${correcaoId}`,
                    {
                        correcao: novoTexto
                    }
                );

                // Atualizar o documento local se for o documento atual
                if (this.documento?.id === documentoId && response.data?.data) {
                    // Atualizar a correção no array de correções
                    if (this.documento.correcoes) {
                        const correcaoIndex = this.documento.correcoes.findIndex(c => c.id === correcaoId);
                        if (correcaoIndex !== -1) {
                            this.documento.correcoes[correcaoIndex].correcao = novoTexto;
                        }
                    }
                }

                return response;
            } catch (error) {
                this.error = 'Erro ao atualizar correção escrita';
                console.error('Erro ao atualizar correção escrita:', error);

                let errorMessage = 'Erro ao atualizar correção escrita. Tente novamente.';

                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                }

                throw new Error(errorMessage);
            }
        },
    },
});
